import { Component, Getter, Vue } from 'nuxt-property-decorator';
import { Context } from '@nuxt/types';
import { TSiteConfigState } from '~/store/types';
import {
  SanityDocumentSchema,
  ISanityDocuments,
  TSanityDocumentPageSchemas,
} from '~/types/sanity-documents';

@Component
export default class SanityDocumentPage extends Vue {
  @Getter('siteConfig')
  readonly siteConfigState!: TSiteConfigState;

  schemas = SanityDocumentSchema;

  created() {
    const params = this.$route.params;
    if (!this.$route.name || !params.slug || !params.schema) {
      return;
    }

    const pageRouteParams = this.$sanityLookup.getPageRouteParams({
      slug: params.slug,
      schema: params.schema,
    });

    if (pageRouteParams?.schema && pageRouteParams.schema !== params.schema) {
      this.$router.replace({
        name: this.$route.name,
        params: pageRouteParams,
        query: this.$route.query,
        hash: this.$route.hash,
      });
    }
  }

  async asyncData({ store, params, $sanityLookup }: Context) {
    if (!params?.slug || !params?.schema) {
      return;
    }

    const { slug } = params;
    const docType = $sanityLookup.getSchemaFromRouteParams(params);

    if (!docType) {
      return;
    }

    if (!store.state?.[docType]?.slugs?.[slug]?.key) {
      $sanityLookup.fetchDocument({
        _type: docType,
        slug,
      });
    }
  }

  get pageRoutePath() {
    return this.$sanityLookup.getPageRoutePath({
      slug: this.pageState?.data?.slug,
      schema: this.pageState?.data?._type as SanityDocumentSchema,
    });
  }

  get pageSchema() {
    return this.$sanityLookup.getSchemaFromRouteParams(this.$route.params);
  }

  get pageState() {
    if (!this.pageSchema) {
      return {
        isLoading: false,
        hasError: false,
      };
    }

    return (
      this.$sanityLookup.getDocumentFromSlug({
        slug: this.$route.params?.slug,
        _type: this.pageSchema,
      }) || {
        isLoading: false,
        hasError: false,
      }
    );
  }

  get slugState() {
    if (!this.pageSchema) {
      return {
        isLoading: false,
        hasError: false,
      };
    }

    return (
      this.$sanityLookup.getSlug({
        slug: this.$route.params?.slug,
        _type: this.pageSchema,
      }) || {
        isLoading: false,
        hasError: false,
      }
    );
  }

  get isLandingPageEnabled() {
    return (
      this.pageState?.data &&
      this.pageAvailability?.isLandingPageEnabled &&
      this.pageAvailabilityPerSchema?.isLandingPageEnabled
    );
  }

  get isCreateAccountPageEnabled() {
    return (
      this.pageState?.data &&
      this.pageAvailability?.isCreateAccountPageEnabled &&
      this.pageAvailabilityPerSchema?.isCreateAccountPageEnabled
    );
  }

  get isUaPageEnabled() {
    return (
      this.pageState?.data &&
      this.pageAvailability?.isUaPageEnabled &&
      this.pageAvailabilityPerSchema?.isUaPageEnabled
    );
  }

  get pageAvailability() {
    return (
      this.pageState?.data as ISanityDocuments[TSanityDocumentPageSchemas]
    )?.pageAvailability;
  }

  get pageAvailabilityPerSchema() {
    return this.siteConfigState?.data?.pageAvailabilityPerSchema?.[
      this.pageSchema as TSanityDocumentPageSchemas
    ]?.pageAvailability;
  }

  get pageNotFound() {
    return (
      (this.pageState &&
        'data' in this.pageState &&
        this.pageState.data === undefined) ||
      !(
        this.isLandingPageEnabled ||
        this.isCreateAccountPageEnabled ||
        this.isUaPageEnabled
      )
    );
  }
}
